import React, { Dispatch, SetStateAction } from "react"
import { useMutation } from "@apollo/client"
import { v4 as uuidv4 } from "uuid"
import { useNavigate } from "react-router-dom"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { LogicState, DiscountRuleInputs } from "@lib/types/discount"
import { hideEditSidebar } from "lib/store/services/editSidebar/slice"
import { useAppDispatch } from "lib/store"
import { transformLogicData } from "helpers/transformLogicData"
import { DiscountRule } from "@lib/types/generated/graphql-types"
import { duplicateLogicRules } from "helpers/duplicateDiscount"

import CREATE_OR_UPDATE_CART_RULE from "graphql/mutations/discount/CreateOrUpdateCartRule"

import { EditPopup } from "components/Ui/EditPopup/EditPopup"
import Input from "components/Ui/Form/Input"

type Props = {
  cartRuleData: DiscountRule
  logicState: LogicState
  setDuplicateCartRule: Dispatch<SetStateAction<boolean>>
}

const DuplicateCartRule = ({ cartRuleData, logicState, setDuplicateCartRule }: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const closeSidebar = () => {
    dispatch(hideEditSidebar())
    setDuplicateCartRule(false)
  }

  const [addCartRule, { loading }] = useMutation(CREATE_OR_UPDATE_CART_RULE, {
    onCompleted: (data) => {
      dispatch(
        alertActions.actions.createAlert({
          message: `New cart rule created. ${cartRuleData.name} successfully duplicated`,
          type: "success"
        })
      )
      navigate(`/discounts/cart-rules/${data.createOrUpdateDiscountRule.id}`)
      closeSidebar()
    },
    onError: () => {
      dispatch(
        alertActions.actions.createAlert({
          type: "error"
        })
      )
    }
  })
  const onSubmit: SubmitHandler<DiscountRuleInputs> = (data) => {
    const { name } = data
    const transformedData = transformLogicData(duplicateLogicRules(logicState))
    addCartRule({
      variables: {
        id: uuidv4(),
        name,
        isActive: cartRuleData.isActive ?? false,
        isStackable: cartRuleData.isStackable ?? false,
        sortOrder: cartRuleData.sortOrder,
        ...transformedData,
        ...(cartRuleData.validDateRange &&
          cartRuleData.validDateRange.from &&
          cartRuleData.validDateRange.to && {
            validDateRange: {
              from: new Date(cartRuleData.validDateRange.from),
              to: new Date(cartRuleData.validDateRange.to)
            }
          })
      }
    })
  }

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<DiscountRuleInputs>()

  return (
    <EditPopup
      title="Duplicate cart rule"
      buttonText="Duplicate"
      handleOkClick={handleSubmit(onSubmit)}
      handleCloseClick={() => closeSidebar()}
      loading={loading}
    >
      <form>
        <Controller
          name="name"
          render={({ field }) => (
            <Input label="Name" errors={errors} {...field} description={"A new name is required"} />
          )}
          defaultValue={`${cartRuleData.name} Copy`}
          control={control}
          rules={{
            required: "This is a required field"
          }}
        />
      </form>
    </EditPopup>
  )
}

export default DuplicateCartRule
